import React from 'react'
import { Box, Typography, Chip } from '@mui/material'
import { Users, BarChart3, Clock, Lightbulb } from 'lucide-react'

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toString()
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function formatHour(hour: number): string {
  if (hour === 0) return '12am'
  if (hour < 12) return `${hour}am`
  if (hour === 12) return '12pm'
  return `${hour - 12}pm`
}

interface Props {
  data: {
    topFollowers: Array<{
      username: string
      fullName: string | null
      profilePicUrl: string | null
      followerCount: number | null
      commentCount: number
    }>
    contentBreakdown: Array<{
      type: string
      postCount: number
      avgLikes: number
      avgComments: number
      avgEngagement: number
    }>
    bestPostingTimes: Array<{
      day: number
      hour: number
      avgEngagement: number
      postCount: number
    }>
    recommendations: string[]
  }
}

export default function GatedReport({ data }: Props): JSX.Element {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
      {/* Top Engaged Followers */}
      <Box sx={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Users size={16} color="#a855f7" />
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Top Engaged Followers
          </Typography>
        </Box>
        {data.topFollowers.length === 0 ? (
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>
            Not enough comment data to identify top followers.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {data.topFollowers.map((follower, i) => (
              <Box key={follower.username} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: '8px', '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '13px', fontWeight: 600, width: 20, textAlign: 'center' }}>
                  {i + 1}
                </Typography>
                {follower.profilePicUrl ? (
                  <Box component="img" src={follower.profilePicUrl} sx={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: 'rgba(168,85,247,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ color: '#a855f7', fontSize: '14px', fontWeight: 600 }}>
                      {follower.username.charAt(0).toUpperCase()}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ color: 'white', fontSize: '13px', fontWeight: 600 }}>@{follower.username}</Typography>
                  {follower.followerCount != null && (
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>
                      {formatNumber(follower.followerCount)} followers
                    </Typography>
                  )}
                </Box>
                <Chip
                  label={`${follower.commentCount} comments`}
                  size="small"
                  sx={{ backgroundColor: 'rgba(168,85,247,0.1)', color: '#a855f7', fontSize: '11px' }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Content Type Breakdown */}
      {data.contentBreakdown.length > 0 && (
        <Box sx={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <BarChart3 size={16} color="#a855f7" />
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Engagement by Content Type
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {data.contentBreakdown.map((item) => (
              <Box key={item.type} sx={{ flex: 1, textAlign: 'center', p: 1.5, backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '20px' }}>
                  {formatNumber(item.avgEngagement)}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', mb: 0.5 }}>avg engagement</Typography>
                <Chip label={item.type} size="small" sx={{ backgroundColor: 'rgba(168,85,247,0.1)', color: '#a855f7', fontSize: '11px' }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '10px', mt: 0.5 }}>
                  {item.postCount} posts
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Best Posting Times */}
      {data.bestPostingTimes.length > 0 && (
        <Box sx={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Clock size={16} color="#a855f7" />
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Best Posting Times
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {data.bestPostingTimes.slice(0, 5).map((slot, i) => (
              <Chip
                key={`${slot.day}-${slot.hour}`}
                label={`${DAY_NAMES[slot.day]} ${formatHour(slot.hour)} (${formatNumber(slot.avgEngagement)} avg)`}
                sx={{
                  backgroundColor: i === 0 ? 'rgba(168,85,247,0.2)' : 'rgba(255,255,255,0.05)',
                  border: i === 0 ? '1px solid rgba(168,85,247,0.4)' : '1px solid rgba(255,255,255,0.08)',
                  color: i === 0 ? 'white' : 'rgba(255,255,255,0.7)',
                  fontSize: '12px',
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Recommendations */}
      {data.recommendations.length > 0 && (
        <Box sx={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Lightbulb size={16} color="#eab308" />
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Growth Recommendations
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {data.recommendations.map((rec, i) => (
              <Typography key={i} sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', lineHeight: 1.5, pl: 2, borderLeft: '2px solid rgba(168,85,247,0.3)' }}>
                {rec}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}
